/**
 * Token packs (SUP / specialist chat) → GA4 ecommerce.
 *
 *   view_item_list  RefillModal opens
 *   select_item     a pack is picked (before the sign-in gate)
 *   begin_checkout  createQuestionsCheckout got a Stripe URL
 *   purchase        features/tokenPurchase: Stripe says paid on the return
 *
 * transaction_id of `purchase` is the Stripe Checkout Session id — the one
 * site_server sends through Measurement Protocol, so GA4 dedups the pair.
 * It goes through trackPurchase like every other web purchase: test sessions
 * only from internal traffic, affiliation "web".
 * Legacy SKUs ("30", "360") have no price here and send nothing.
 */
import { trackEcommerce, trackPurchase, type EcommerceItem } from "@/shared/lib/analytics";
import { getTokenPack, TOKEN_PACKS, type TokenPack } from "./tokenPacks";

export const TOKEN_PACK_LIST_ID = "sup_token_packs";
const LIST_NAME = "SUP token packs";
const CATEGORY = "sup_tokens";

const item = (pack: TokenPack, index?: number): EcommerceItem => ({
    item_id: pack.sku,
    item_name: `SUP ${pack.count} tokens`,
    item_category: CATEGORY,
    item_brand: "torsunov",
    item_list_id: TOKEN_PACK_LIST_ID,
    item_list_name: LIST_NAME,
    ...(index !== undefined ? { index } : {}),
    price: pack.priceCents / 100,
    quantity: 1,
});

/** The pack by sku, else by token count (counts are unique on the ladder). */
export const resolveTokenPack = (sku?: string | null, count?: number | null): TokenPack | undefined =>
    (sku ? getTokenPack(sku) : undefined) ??
    (typeof count === "number" ? TOKEN_PACKS.find((p) => p.count === count) : undefined);

/** `value` = the sum of the listed packs' prices (what the list offers). */
export const trackTokenPackList = (): void => {
    const cents = TOKEN_PACKS.reduce((sum, p) => sum + p.priceCents, 0);
    trackEcommerce(
        "view_item_list",
        { currency: "USD", value: cents / 100, items: TOKEN_PACKS.map((p, i) => item(p, i)) },
        { item_list_id: TOKEN_PACK_LIST_ID, item_list_name: LIST_NAME },
    );
};

export const trackTokenPackSelect = (sku: string): void => {
    const pack = resolveTokenPack(sku);
    if (!pack) return;
    trackEcommerce(
        "select_item",
        { currency: "USD", value: pack.priceCents / 100, items: [item(pack, TOKEN_PACKS.indexOf(pack))] },
        { item_list_id: TOKEN_PACK_LIST_ID, item_list_name: LIST_NAME },
    );
};

export const trackTokenPackCheckout = (sku: string): void => {
    const pack = resolveTokenPack(sku);
    if (!pack) return;
    trackEcommerce("begin_checkout", { currency: "USD", value: pack.priceCents / 100, items: [item(pack)] }, { kind: CATEGORY });
};

/** Once per session id on this device (trackEcommerce's purchase latch). */
export const trackTokenPackPurchase = (sessionId: string, sku?: string | null, count?: number | null): void => {
    const pack = resolveTokenPack(sku, count);
    if (!pack || !sessionId) return;
    trackPurchase(sessionId, { kinds: [CATEGORY], fallback: { kind: CATEGORY, items: [item(pack)] } });
};
